import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  makeStyles,
  tokens,
} from '@pacxa/ui';
import { Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { PrincipalProvider, usePrincipal } from './auth/PrincipalProvider.js';
import { RequirePermission } from './auth/RequirePermission.js';
import { SignInPage } from './auth/SignInPage.js';
import { AppShell } from './components/AppShell.js';
import { MODULE_COMPONENTS } from './modules/registry.js';

const useStyles = makeStyles({
  centred: {
    display: 'flex',
    flexGrow: 1,
    minHeight: '100vh',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  loading: {
    display: 'flex',
    minHeight: '384px',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorBox: {
    maxWidth: '520px',
  },
});

function LoadingScreen({ label }: { label: string }) {
  const styles = useStyles();
  // Spinner renders its own label; the wrapper keeps the announcement
  // behaviour the hand-rolled version had.
  return (
    <div className={styles.loading} role="status" aria-live="polite">
      <Spinner label={label} labelPosition="below" />
    </div>
  );
}

function AuthenticatedRoutes() {
  const styles = useStyles();
  const { me, isLoading, error } = usePrincipal();

  if (isLoading) return <LoadingScreen label="Loading your access" />;

  if (error) {
    return (
      <div className={styles.centred}>
        <MessageBar intent="error" className={styles.errorBox}>
          <MessageBarBody>
            <MessageBarTitle>Couldn&rsquo;t load your account</MessageBarTitle>
            We signed you in but couldn&rsquo;t load your permissions. Reload the page; if this
            continues, contact IT.
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  // Land on the first module the user can actually see, rather than assuming
  // everyone has the dashboard.
  const landing = me?.modules[0]?.route ?? '/';

  return (
    <AppShell>
      <Suspense fallback={<LoadingScreen label="Loading module" />}>
        <Routes>
          {MODULE_COMPONENTS.map(({ id, permission, component: Component }) => {
            const mod = me?.modules.find((m) => m.id === id);
            if (!mod) return null;
            return (
              <Route
                key={id}
                path={mod.route === '/' ? '/' : `${mod.route}/*`}
                element={
                  <RequirePermission permission={permission}>
                    <Component />
                  </RequirePermission>
                }
              />
            );
          })}
          <Route path="*" element={<Navigate to={landing} replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

export function App() {
  // BASE_URL is '/' or e.g. '/v2/'. Router wants it without the trailing slash.
  const basename = import.meta.env.BASE_URL.replace(/\/$/, '');

  return (
    <BrowserRouter basename={basename}>
      <UnauthenticatedTemplate>
        <SignInPage />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <PrincipalProvider>
          <AuthenticatedRoutes />
        </PrincipalProvider>
      </AuthenticatedTemplate>
    </BrowserRouter>
  );
}
